import * as ExcelJS from "exceljs"
import { saveAs } from "file-saver"
import moment from "moment"

/**
 * Allows generate an xlsx file from the params that you provide
 *
 * @param {string[]} [columns] - An array of strings that will be the column names.
 * @param { { [ key in string ]: string }[] } [rows] - an array of objects, each object representing a row in the table.
 * @param {string} [title] - The title of the excel file.
 * @param { string | undefined } [fileName] - The name of the file to be downloaded.
 *
 * @returns { Promise<void> }
 */
export const exportTableToXLSX = async (
  columns = [],
  rows = [],
  title = "",
  fileName = ""
) => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet(title)
  const titleInfo = worksheet.getCell("A1")
  titleInfo.value = title
  titleInfo.font = { size: 20, bold: true }

  worksheet.addTable({
    name: title,
    ref: `A${worksheet?.lastRow?._number + 2}`,
    headerRow: true,
    style: {
      showRowStripes: true,
      theme: "TableStyleLight1"
    },
    columns: columns.map(c => ({ name: c, width: 15 })),
    rows: rows.map(row => {
      const sortedRowFields = []
      columns.forEach(column => sortedRowFields.push(row[column]))
      return sortedRowFields
    })
  })

  const dateLabel = worksheet.getCell(`A${worksheet.lastRow._number + 2}`)
  dateLabel.value = `Fecha de impresión: ${moment(new Date).format("DD/MM/YYYY")}`
  dateLabel.font = { bold: true, size: 10 }

  const buf = await workbook.xlsx.writeBuffer()
  saveAs(new Blob([buf]), `${fileName || title}.xlsx`)
}

export const applyHeaderStyle = (worksheet, rowNumber) => {
  worksheet.getRow(rowNumber).eachCell(cell => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF959391" }
    };

    cell.font = {
      bold: true,
      color: { argb: "FFFFFFFF" }
    }
  })
}

export const exportTableMonitoristasToXLSX = async (
  columns = [],
  rows = [],
  title = "",
  fileName = "",
  fechas = []
) => {
  if (!columns.length || !rows.length || !title) {
    throw new Error("Faltan parámetros requeridos para generar el archivo.");
  }

  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(title);

    worksheet.mergeCells("A1:C1");
    const titleInfo = worksheet.getCell("A1");
    titleInfo.value = title;
    titleInfo.font = { size: 20, bold: true };

    let currentRowNumber = 3; 
    const columnsBeforeInfo = columns.slice(0, columns.indexOf("information")); 

    rows.forEach((row) => {
      const maxSplitRows = Math.max(
        ...columns.map((col) => (Array.isArray(row[col]) ? row[col].length : 1))
      );

      for (let i = 0; i < maxSplitRows; i++) {
        const isDeliveryPeriod = row["information"]?.[i] === "Periodo a entregar"
        const excelRow = worksheet.getRow(currentRowNumber + i)
    
        const months = fechas
    
        const monthsWithValue = months
          .map((month, idx) => {
            const value = row[month]
            const cellValue = Array.isArray(value) ? value[i] : value;
            return cellValue ? { month, index: idx } : null;
          })
          .filter(Boolean)
    
        let startMonthIndex = -1
        let endMonthIndex = -1
        let range = ""
    
        if (monthsWithValue.length) {
          startMonthIndex = monthsWithValue[0].index;
          endMonthIndex = monthsWithValue[monthsWithValue.length - 1].index;
    
          if (startMonthIndex === endMonthIndex) {
            range = months[startMonthIndex]
          } else {
            range = `${months[startMonthIndex]}-${months[endMonthIndex]}`;
          }
        }
    
        columns.forEach((col, colIndex) => {
          if (Array.isArray(row[col])) {
            if (fechas.includes(col)) {
              if (!isDeliveryPeriod) {
                excelRow.getCell(colIndex + 1).value = row[col][i] || "";
                return;
              }
    
              const currentMonthIndex = months.indexOf(col);
    
              if (currentMonthIndex === startMonthIndex) {
                excelRow.getCell(colIndex + 1).value = "";
              } else if (currentMonthIndex === endMonthIndex) {
                excelRow.getCell(colIndex + 1).value = range;
              } else {
                excelRow.getCell(colIndex + 1).value = "";
              }
            } else {
              excelRow.getCell(colIndex + 1).value = row[col][i] || "";
            }
          } else if (i === 0) {
            excelRow.getCell(colIndex + 1).value = row[col];
          }
        });
      }

     
      columnsBeforeInfo.forEach((col, colIndex) => {
        worksheet.mergeCells(
          currentRowNumber,
          colIndex + 1,
          currentRowNumber + maxSplitRows - 1,
          colIndex + 1
        );
      });

      
      currentRowNumber += maxSplitRows;
    });

    
    const headerRow = worksheet.getRow(2);
    columns.forEach((col, colIndex) => {
      const cell = headerRow.getCell(colIndex + 1);
      cell.value = col;

      /**
       * Para futuros cambios de diseño o colores aqui se coloca el color a los encabezados UwU
       */
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } }; // Texto headers
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "8C1E21" }, // Fondo headers
      };
      cell.alignment = { vertical: "middle", horizontal: "center" }; // Alineación
    });

    headerRow.height = 20;

    
    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.alignment = { wrapText: true, vertical: "middle", horizontal: "left" };
      });
    });

   
    columns.forEach((column, colIndex) => {
      let maxWidth = column.length;
      rows.forEach((row) => {
        const cellValue = row[column];
        if (Array.isArray(cellValue)) {
          maxWidth = Math.max(maxWidth, cellValue.join(", ").length);
        } else if (cellValue) {
          maxWidth = Math.max(maxWidth, cellValue.toString().length);
        }
      });
      worksheet.getColumn(colIndex + 1).width =
        column === "information" || fechas.includes(column) ? 50 : maxWidth + 2;
    });

    
    const dateLabel = worksheet.getCell(`A${currentRowNumber + 2}`);
    dateLabel.value = `Fecha de impresión: ${moment(new Date()).format(
      "DD/MM/YYYY"
    )}`;
    dateLabel.font = { bold: true, size: 10 };

   
    const buf = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `${fileName || title}.xlsx`);
  } catch (error) {
    console.error("Error al generar el archivo Excel:", error);
  }
};
